import { useEffect, useMemo } from 'react'
import { getGridSingleSelectOperators, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import {
  ContentReviewRequest,
  TrackingsSnaphotResponse,
  TrackingUser,
} from '@tabeeb/contracts/main-api/swagger-main-api'
import { Caption, DateInfo, IconButton, Stack, Tooltip } from '@collaborate-health/uikit'
import { InfiniteData } from '@tanstack/react-query'
import { DeleteOutline } from '@mui/icons-material'
import { UserQualificationStatusChip } from '@tabeeb/modules/qualifications/components'
import { CertificateStatusChip } from '@tabeeb/modules/certificates/components'
import {
  CertificateStatusType,
  UserAssignedQualificationStatusType,
} from '@tabeeb/contracts/microservices/swagger-certificate-service'
import { useDebouncedState } from '@tabeeb/shared/utils/hooks'
import { useSelector } from '@tabeeb/store'
import { getLatestNotCompletedContentReview } from '@tabeeb/modules/contentReviews/selectors'
import { sessionTitleFormatter } from '@tabeeb/shared/utils/text'
import { ContentCheckInsTabViews } from '@tabeeb/modules/checkIns/components/ContentCheckInsTab/constants'
import { CertificateImageContainer, FilterTextField } from './CheckInsTable.styles'
import { CheckInRow } from './CheckInsTable.types'
import { getCertificate, getQualification } from './CheckInsTable.helpers'

interface UseCheckInsTableColumnsProps {
  checkIns: InfiniteData<TrackingsSnaphotResponse>
  viewType: ContentCheckInsTabViews
  onRemoveUserRecord: (user: TrackingUser) => void
  onOpenImageViewer: (url: string) => void
}

const simpleTextFilterOperator = {
  label: 'contains',
  value: 'contains',
  getApplyFilterFn: () => {
    return null
  },
  InputComponent: ({ item, applyValue }: any) => {
    const [inputValue, debouncedValue, setInputValue] = useDebouncedState(item.value)

    useEffect(() => {
      if (debouncedValue !== item.value) {
        applyValue({ ...item, value: debouncedValue })
      }
    }, [applyValue, debouncedValue, item])

    return (
      <FilterTextField
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder='Filter value'
        variant='standard'
        size='small'
        fullWidth
      />
    )
  },
}

const useCheckInsTableColumns = ({
  checkIns,
  viewType,
  onRemoveUserRecord,
  onOpenImageViewer,
}: UseCheckInsTableColumnsProps): GridColDef<CheckInRow>[] => {
  const latestReview = useSelector<ContentReviewRequest>(getLatestNotCompletedContentReview)

  const claimKeys = useMemo(() => {
    const set = new Set<string>()
    checkIns.pages.forEach((p) => p.Users?.forEach((u) => u.Claims?.forEach((c) => c.Type && set.add(c.Type))))
    return Array.from(set)
  }, [checkIns.pages])

  const columns = useMemo<GridColDef<CheckInRow>[]>(() => {
    const cols: GridColDef<CheckInRow>[] = []

    cols.push({
      field: 'dateSubmitted',
      headerName: 'Date submitted',
      sortable: false,
      filterable: false,
      width: 130,
      renderCell: (params: GridRenderCellParams<CheckInRow>) => {
        if (params.row.isRoot) {
          return <DateInfo date={params.row.CreatedOn} />
        }
        return null
      },
    })

    cols.push({
      field: 'name',
      headerName: 'Name',
      sortable: false,
      filterable: true,
      width: 200,
      filterOperators: [simpleTextFilterOperator],
      renderCell: (params: GridRenderCellParams<CheckInRow>) => {
        if (!params.row.isRoot) {
          return null
        }

        let value
        if (params.row.UserName && params.row.UserName.length > 1) {
          value = params.row.UserName
        } else if (params.row.UserEmail && params.row.UserEmail.length > 1) {
          value = params.row.UserEmail
        } else {
          value = '-'
        }
        return (
          <Stack>
            <Tooltip text={value}>
              <Caption>{value}</Caption>
            </Tooltip>
            <Tooltip text={params.row.UserEmail}>
              <Caption>{params.row.UserEmail}</Caption>
            </Tooltip>
          </Stack>
        )
      },
    })

    claimKeys.forEach((claimKey) => {
      cols.push({
        field: `claim:${claimKey}`,
        headerName: claimKey,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<CheckInRow>) => {
          if (!params.row.isRoot) {
            return null
          }
          const value = (params.row.Claims ?? []).find((c) => c.Type === claimKey)?.Value ?? '-'
          return (
            <Tooltip text={value}>
              <Caption noWrap>{value}</Caption>
            </Tooltip>
          )
        },
      })
    })

    cols.push({
      field: 'qualifications',
      headerName: 'Qualifications',
      sortable: false,
      filterable: true,
      width: 170,
      filterOperators: [simpleTextFilterOperator],
      renderCell: (params: GridRenderCellParams<CheckInRow>) => {
        if (!params.row.isRoot && !params.row.isFirstCertOfQual) {
          return null
        }

        const qual = getQualification(params.row)
        if (!qual) {
          return <Caption>-</Caption>
        }
        return <Caption>{qual.Name}</Caption>
      },
    })

    cols.push({
      field: 'qualificationStatus',
      headerName: 'Qualification status',
      width: 170,
      sortable: false,
      filterable: true,
      type: 'singleSelect',
      valueOptions: [
        { value: UserAssignedQualificationStatusType.Pending, label: 'Pending' },
        { value: UserAssignedQualificationStatusType.Active, label: 'Active' },
        { value: UserAssignedQualificationStatusType.Revoked, label: 'Revoked' },
        { value: UserAssignedQualificationStatusType.CertificateExpired, label: 'Certificate expired' },
        { value: UserAssignedQualificationStatusType.VersionExpired, label: 'Version expired' },
        { value: UserAssignedQualificationStatusType.CertificateTypeDeleted, label: 'Certificate type deleted' },
        { value: UserAssignedQualificationStatusType.QualificationDeleted, label: 'Qualification deleted' },
        { value: 99, label: 'Missing' },
      ],
      filterOperators: getGridSingleSelectOperators().filter((operator) => operator.value === 'is'),
      renderCell: (params: GridRenderCellParams<CheckInRow>) => {
        if (!params.row.isRoot && !params.row.isFirstCertOfQual) {
          return null
        }

        const qual = getQualification(params.row)
        if (!qual) {
          return <Caption>-</Caption>
        }
        return <UserQualificationStatusChip status={qual.Status} />
      },
    })

    cols.push({
      field: 'certificateType',
      headerName: 'Certificate type',
      sortable: false,
      filterable: true,
      width: 170,
      filterOperators: [simpleTextFilterOperator],
      renderCell: (params: GridRenderCellParams<CheckInRow>) => {
        const cert = getCertificate(params.row)
        if (!cert) {
          return <Caption>-</Caption>
        }
        return <Caption>{cert.TypeName}</Caption>
      },
    })

    cols.push({
      field: 'certificateStatus',
      headerName: 'Certificate status',
      width: 170,
      sortable: false,
      filterable: true,
      type: 'singleSelect',
      valueOptions: [
        { value: CertificateStatusType.PendingReview, label: 'Pending review' },
        { value: CertificateStatusType.Approved, label: 'Approved' },
        { value: CertificateStatusType.Rejected, label: 'Rejected' },
        { value: CertificateStatusType.Expired, label: 'Expired' },
        { value: CertificateStatusType.Draft, label: 'Draft' },
        { value: CertificateStatusType.Obsolete, label: 'Obsolete' },
        { value: CertificateStatusType.Processed, label: 'Processed' },
        { value: 99, label: 'Missing' },
      ],
      filterOperators: getGridSingleSelectOperators().filter((operator) => operator.value === 'is'),
      renderCell: (params: GridRenderCellParams<CheckInRow>) => {
        const cert = getCertificate(params.row)
        if (!cert) {
          return <Caption>-</Caption>
        }
        return <CertificateStatusChip status={cert.Status} />
      },
    })

    cols.push({
      field: 'image',
      headerName: 'Image',
      width: 170,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<CheckInRow>) => {
        const cert = getCertificate(params.row)

        if (cert?.Url) {
          return (
            <CertificateImageContainer
              onClick={() => {
                onOpenImageViewer(cert.Url!)
              }}
            >
              <input
                alt='Certificate image'
                title='Open image'
                style={{ maxWidth: 100, maxHeight: 80, pointerEvents: 'none' }}
                type='image'
                src={cert.Url}
              />
            </CertificateImageContainer>
          )
        }
        return <Caption>-</Caption>
      },
    })

    cols.push({
      field: 'validFrom',
      headerName: 'Valid from',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<CheckInRow>) => {
        const cert = getCertificate(params.row)
        if (cert?.ValidFrom) {
          return <DateInfo date={cert.ValidFrom} />
        }
        return <Caption>-</Caption>
      },
    })

    cols.push({
      field: 'validTill',
      headerName: 'Valid till',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<CheckInRow>) => {
        const cert = getCertificate(params.row)
        if (cert?.ValidTo) {
          return <DateInfo date={cert.ValidTo} />
        }
        return <Caption>-</Caption>
      },
    })

    cols.push({
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<CheckInRow>) => {
        if (!params.row.isRoot) {
          return null
        }

        let isDeleteButtonDisabled = false
        if (viewType === ContentCheckInsTabViews.OnReview) {
          isDeleteButtonDisabled = true
        } else if (latestReview) {
          isDeleteButtonDisabled = Date.parse(latestReview.CreatedOn) >= Date.parse(params.row.CreatedOn)
        }
        return (
          <Tooltip
            text={
              isDeleteButtonDisabled
                ? sessionTitleFormatter.format('This record cannot be deleted as it is a part of a session review')
                : 'Remove record'
            }
          >
            <span style={{ display: 'inline-flex' }}>
              <IconButton
                size='small'
                onClick={() => onRemoveUserRecord(params.row)}
                disabled={isDeleteButtonDisabled}
                color={isDeleteButtonDisabled ? 'default' : 'error'}
              >
                <DeleteOutline fontSize='small' color='inherit' />
              </IconButton>
            </span>
          </Tooltip>
        )
      },
    })

    return cols
  }, [claimKeys, latestReview, onOpenImageViewer, onRemoveUserRecord, viewType])

  return columns
}

export default useCheckInsTableColumns

